import React, { useEffect, useRef, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { motion } from 'framer-motion';
import { MapPin, AlertTriangle } from 'lucide-react';
import { MAPBOX_CONFIG, isMapboxAvailable } from '../../config/maps';
import './LocationsMap.css';

// Set Mapbox access token from config
mapboxgl.accessToken = MAPBOX_CONFIG.ACCESS_TOKEN;

// Simple coordinate cache
const coordinateCache = new Map();

// Simplified geocoding service
class LocationGeocoder {
  static async getCoordinates(location) {
    const cacheKey = `${location.id}-${location.google_address || location.name}`;

    if (coordinateCache.has(cacheKey)) {
      return coordinateCache.get(cacheKey);
    }

    let coordinates = null;

    // Priority 1: Use existing coordinates if valid
    if (location.latitude && location.longitude) {
      const lat = parseFloat(location.latitude);
      const lng = parseFloat(location.longitude);

      if (this.isValidCoordinate(lng, lat)) {
        coordinates = [lng, lat];
      }
    }

    // Priority 2: Geocode Google address
    if (!coordinates && location.google_address) {
      coordinates = await this.geocodeAddress(location.google_address);
    }

    // Priority 3: Geocode location name
    if (!coordinates && location.name) {
      coordinates = await this.geocodeAddress(location.name);
    }

    if (coordinates) {
      coordinateCache.set(cacheKey, coordinates);
    }

    return coordinates;
  }

  static isValidCoordinate(lng, lat) {
    return (
      typeof lng === 'number' && 
      typeof lat === 'number' && 
      !isNaN(lng) && 
      !isNaN(lat) &&
      lng >= -180 && 
      lng <= 180 && 
      lat >= -90 && 
      lat <= 90
    );
  }

  static async geocodeAddress(address) {
    if (!address?.trim() || !isMapboxAvailable()) return null;

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_CONFIG.ACCESS_TOKEN}&limit=1&country=US`
      );

      if (!response.ok) return null;

      const data = await response.json();

      if (data.features?.[0]?.center) {
        const coords = data.features[0].center;
        if (this.isValidCoordinate(coords[0], coords[1])) {
          return coords;
        }
      }

      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }
}

// Early return if no token is available
const LocationsMap = ({ locations = [], className = "" }) => {
  // Don't render map if no token is configured
  if (!isMapboxAvailable()) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`relative rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 ${className}`}
        style={{ height: '350px' }}
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <MapPin className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600 text-sm">Map view temporarily unavailable</p>
            <p className="text-gray-500 text-xs mt-1">{locations.length} location{locations.length !== 1 ? 's' : ''} configured</p>
          </div>
        </div>
      </motion.div>
    );
  }

  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [validLocations, setValidLocations] = useState([]);

  // Validate Mapbox token
  useEffect(() => {
    if (!isMapboxAvailable()) {
      setError('Mapbox token not configured');
      return;
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || error) return;

    try {
      // Check if Mapbox GL JS is supported
      if (!mapboxgl.supported()) {
        setError('Your browser does not support Mapbox GL JS');
        return;
      }

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: MAPBOX_CONFIG.DEFAULT_STYLE,
        center: MAPBOX_CONFIG.DEFAULT_CENTER,
        zoom: MAPBOX_CONFIG.DEFAULT_ZOOM,
        attributionControl: false,
        logoPosition: 'bottom-right'
      });

      map.current.on('load', () => {
        setMapLoaded(true);
        console.log('Map loaded successfully');
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e);
        setError('Failed to load map');
      });

    } catch (err) {
      console.error('Map initialization error:', err);
      setError('Failed to initialize map');
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [error]);

  // Process locations and add markers
  useEffect(() => {
    if (!map.current || !mapLoaded || !locations.length) return;

    const processLocations = async () => {
      try {
        // Clear existing markers
        markers.current.forEach(marker => marker.remove());
        markers.current = [];

        // Get coordinates for all locations
        const locationPromises = locations.map(async (location) => {
          const coordinates = await LocationGeocoder.getCoordinates(location);
          return coordinates ? { ...location, coordinates } : null;
        });

        const results = await Promise.all(locationPromises);
        const locationsWithCoords = results.filter(Boolean);

        setValidLocations(locationsWithCoords);

        if (locationsWithCoords.length === 0) {
          console.warn('No locations could be geocoded');
          return;
        }

        // Create bounds for fitting the map
        const bounds = new mapboxgl.LngLatBounds();

        // Add markers
        locationsWithCoords.forEach((location) => {
          const { coordinates } = location;
          const isVerified = !!(location.google_places_id && location.google_address);

          // Create marker element
          const el = document.createElement('div');
          el.className = 'custom-marker';

          // Create inner element for hover effects
          const innerEl = document.createElement('div');
          innerEl.style.cssText = `
            width: 30px;
            height: 30px;
            border-radius: 50%;
            background: ${isVerified ? '#10b981' : '#f59e0b'};
            border: 2px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.2s;
          `;

          innerEl.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          `;

          el.appendChild(innerEl);

          // Add hover effect to inner element
          el.addEventListener('mouseenter', () => {
            innerEl.style.transform = 'scale(1.1)';

            // Show location name tooltip
            const tooltip = document.createElement('div');
            tooltip.style.cssText = `
              position: absolute;
              bottom: 40px;
              left: 50%;
              transform: translateX(-50%);
              background: white;
              color: #111827;
              padding: 6px 12px;
              border-radius: 8px;
              font-size: 12px;
              font-weight: 500;
              white-space: nowrap;
              pointer-events: none;
              z-index: 1000;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
              border: 1px solid #f3f4f6;
            `;
            tooltip.textContent = location.name;
            tooltip.className = 'marker-tooltip';
            el.appendChild(tooltip);
          });
          el.addEventListener('mouseleave', () => {
            innerEl.style.transform = 'scale(1)';

            // Remove tooltip
            const tooltip = el.querySelector('.marker-tooltip');
            if (tooltip) tooltip.remove();
          });

          // Create marker - Mapbox handles outer positioning
          const marker = new mapboxgl.Marker(el)
            .setLngLat(coordinates)
            .addTo(map.current);

          // Create popup
          const popup = new mapboxgl.Popup({
            offset: 25,
            closeButton: false
          }).setHTML(`
            <div style="padding: 8px; font-family: system-ui;">
              <div style="font-weight: 600; margin-bottom: 4px;">${location.name}</div>
              <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
                ${isVerified ? '✓ Verified' : '⚠ Pending Verification'}
              </div>
              ${location.google_address ? `<div style="font-size: 11px; color: #888;">${location.google_address}</div>` : ''}
            </div>
          `);

          el.addEventListener('click', () => {
            popup.addTo(map.current);
          });

          markers.current.push(marker);
          bounds.extend(coordinates);
        });

        // Fit map to show all markers
        if (locationsWithCoords.length === 1) {
          map.current.setCenter(locationsWithCoords[0].coordinates);
          map.current.setZoom(14);
        } else {
          map.current.fitBounds(bounds, {
            padding: 50,
            maxZoom: 15
          });
        }

      } catch (err) {
        console.error('Error processing locations:', err);
        setError('Failed to load location markers');
      }
    };

    processLocations();
  }, [locations, mapLoaded]);

  // Don't render if no locations
  if (!locations.length) {
    return null;
  }

  // Error state
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative rounded-2xl overflow-hidden border border-red-200 bg-red-50 ${className}`}
        style={{ height: '350px' }}
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm ${className}`}
      style={{ height: '350px' }}
    >
      <div ref={mapContainer} className="w-full h-full" />

      {/* Location count overlay */}
      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
        <div className="flex items-center space-x-2">
          <MapPin size={14} className="text-gray-600" />
          <span className="text-sm font-medium text-gray-800">
            {validLocations.length} of {locations.length} Location{locations.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-xs text-gray-600">Verified</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-xs text-gray-600">Pending</span>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {!mapLoaded && !error && (
        <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <MapPin className="w-6 h-6 text-gray-400" />
          </motion.div>
        </div>
      )}

      {/* Warning for unmapped locations */}
      {validLocations.length < locations.length && (
        <div className="absolute top-4 right-4 bg-amber-50/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm border border-amber-200">
          <div className="flex items-center space-x-2">
            <AlertTriangle size={14} className="text-amber-600" />
            <span className="text-xs text-amber-700">
              {locations.length - validLocations.length} location{locations.length - validLocations.length !== 1 ? 's' : ''} need verification
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default LocationsMap; 